import { useMemo } from 'react';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

import { PartnerOverview } from '@gn-audio/jss-core';
import { Option } from '@gn-audio/jss-core/common/models';

import JabraDropdown from 'common/components/JabraDropdown';

import { ExpertiseOption, OBSOLETE_CERTIFIED_ID } from './constants';

const EXPERTISE_OPTIONS = [
  { id: '655770001-video', type: 'video', label: 'Premium Video Expert', value: '655770001' },
  { id: '655770000-video', type: 'video', label: 'Platinum Video Expert', value: '655770000' },
  { id: '655770001-audio', type: 'audio', label: 'Premium Audio Expert', value: '655770001' },
  { id: '655770000-audio', type: 'audio', label: 'Platinum Audio Expert', value: '655770000' },
] as ExpertiseOption[];

type PartnerLocatorFiltersProps = {
  partners: PartnerOverview[];
  showFilters: boolean;
  dropdownTitle?: Field<string>;
  expertiseFilterTitle?: Field<string>;
  expertiseAllFilterTitle?: Field<string>;
  countriesOptions: Option[];
  selectedCountry: Option | null;
  setSelectedCountry: (country: Option | null) => void;
  selectedExpertise: ExpertiseOption | null;
  setSelectedExpertise: (expertise: ExpertiseOption | null) => void;
  isLoading: boolean;
};

export const PartnerLocatorFilters = ({
  partners,
  showFilters,
  dropdownTitle,
  expertiseFilterTitle,
  expertiseAllFilterTitle,
  countriesOptions,
  selectedCountry,
  setSelectedCountry,
  selectedExpertise,
  setSelectedExpertise,
  isLoading,
}: PartnerLocatorFiltersProps) => {
  const expertiseOptions = useMemo(() => {
    const hasVideoExpertise = partners
      .filter((x) => x.gnh_videopartnerprogram !== OBSOLETE_CERTIFIED_ID)
      .some((partner) => partner.gnh_videopartnerprogram);
    const hasAudioExpertise = partners
      .filter((x) => x.gnh_audiopartnerprogram !== OBSOLETE_CERTIFIED_ID)
      .some((partner) => partner.gnh_audiopartnerprogram);

    if (hasVideoExpertise && hasAudioExpertise) {
      return EXPERTISE_OPTIONS;
    } else if (hasVideoExpertise) {
      return EXPERTISE_OPTIONS.filter((x) => x.type === 'video');
    } else if (hasAudioExpertise) {
      return EXPERTISE_OPTIONS.filter((x) => x.type === 'audio');
    } else {
      return [];
    }
  }, [partners]);

  return (
    <div
      className={classNames('mb-4 flex w-full flex-col gap-3 md:w-1/3', {
        'hidden md:block': !showFilters,
      })}
    >
      <div className="mb-1 font-bold">{dropdownTitle?.value}</div>
      <div className="mb-5 rounded-md border border-gray-300 py-2 pl-3 pr-2">
        <JabraDropdown
          searchInput
          label={dropdownTitle?.value}
          options={countriesOptions}
          selectedOption={selectedCountry}
          onOptionSelect={(option) => setSelectedCountry(option)}
          containerClassNames="min-w-40 md:min-w-50"
          disabled={isLoading}
        />
      </div>

      <div className="mb-1 font-bold">{expertiseFilterTitle?.value}</div>
      <fieldset>
        <legend className="sr-only">{expertiseFilterTitle?.value}</legend>
        <div>
          <input
            type="radio"
            name="expertise"
            value=""
            id="expertise-all"
            checked={!selectedExpertise}
            onChange={() => setSelectedExpertise(null)}
          />
          <label htmlFor="expertise-all" className="ml-2">
            {expertiseAllFilterTitle?.value || 'All Partners'}
          </label>
        </div>
        {expertiseOptions.map((expertise: ExpertiseOption) => (
          <div key={expertise.id}>
            <input
              type="radio"
              name="expertise"
              value={expertise.id}
              id={expertise.id}
              checked={selectedExpertise?.id === expertise.id}
              onChange={() => setSelectedExpertise(expertise)}
            />
            <label htmlFor={expertise.id} className="ml-2">
              {expertise.label}
            </label>
          </div>
        ))}
      </fieldset>
    </div>
  );
};
